import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, ConfirmModal } from '@safetyhub/ui';
import { useToastStore } from '../stores/toast';
import {
  COMPANY_RBAC_QUERY_KEYS,
  companyRbacApi,
  companyRbacErrorMessage,
  type AdminCompanyRoleView,
  type AdminCreateCompanyRoleRequest,
  type AdminUpdateCompanyRoleRequest,
} from '../lib/company-rbac';
import CompanyRoleTable from '../components/company-roles/CompanyRoleTable';
import CompanyRoleFormModal, { type CompanyRoleFormMode } from '../components/company-roles/CompanyRoleFormModal';
import PermissionDefinitionsSection from '../components/company-roles/PermissionDefinitionsSection';

type Tab = 'roles' | 'permissions';

/**
 * 조직 역할·권한 (줄기 company-rbac P3 — `admin/rbac/**`). ⚠️ `/roles`(관리자 역할 관리)는 **슈퍼관리자** 역할이고
 * 여기는 조직이 멤버에게 배정하는 **시스템 역할**과 22개 권한 정의다.
 *
 * ① 시스템 역할 표(행 클릭 → ② 편집 모달: 속성 + 권한 매트릭스, dirty 표준) · ③ 권한 정의 탭(`orgAdjustable` 토글, 끄기는
 * 확인 모달) · ④ 보관/복구(활성 배정이 있으면 서버 409 문구를 토스트로).
 */
export default function CompanyRoles() {
  const queryClient = useQueryClient();
  const showToast = useToastStore((s) => s.showToast);
  const [tab, setTab] = useState<Tab>('roles');
  const [formMode, setFormMode] = useState<CompanyRoleFormMode | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [pendingArchive, setPendingArchive] = useState<AdminCompanyRoleView | null>(null);
  const [busyRoleKey, setBusyRoleKey] = useState<string | null>(null);
  const [busyPermissionKey, setBusyPermissionKey] = useState<string | null>(null);

  const permissionsQuery = useQuery({
    queryKey: COMPANY_RBAC_QUERY_KEYS.permissions,
    queryFn: () => companyRbacApi.listPermissions(),
  });
  const rolesQuery = useQuery({
    queryKey: COMPANY_RBAC_QUERY_KEYS.roles,
    queryFn: () => companyRbacApi.listRoles(),
  });

  const invalidateRoles = () => queryClient.invalidateQueries({ queryKey: COMPANY_RBAC_QUERY_KEYS.roles });

  const createMutation = useMutation({
    mutationFn: (body: AdminCreateCompanyRoleRequest) => companyRbacApi.createRole(body),
    onSuccess: async (created) => {
      showToast(`「${created.name}」 역할을 추가했습니다.`, 'success');
      setFormMode(null);
      setFormError(null);
      await invalidateRoles();
    },
    onError: (err: unknown) => setFormError(companyRbacErrorMessage(err, '역할 추가에 실패했습니다.')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ key, body }: { key: string; body: AdminUpdateCompanyRoleRequest }) => companyRbacApi.updateRole(key, body),
    onSuccess: async (updated) => {
      showToast(`「${updated.name}」 역할을 저장했습니다.`, 'success');
      setFormMode(null);
      setFormError(null);
      await invalidateRoles();
    },
    onError: (err: unknown) => setFormError(companyRbacErrorMessage(err, '역할 저장에 실패했습니다.')),
  });

  const handleArchive = async () => {
    if (!pendingArchive) return;
    const role = pendingArchive;
    setBusyRoleKey(role.key);
    try {
      await companyRbacApi.archiveRole(role.key);
      showToast(`「${role.name}」 역할을 보관했습니다. 조직 화면에서 사라집니다.`, 'success');
      setPendingArchive(null);
      await invalidateRoles();
    } catch (err) {
      showToast(companyRbacErrorMessage(err, '보관에 실패했습니다.'), 'error');
    } finally {
      setBusyRoleKey(null);
    }
  };

  const handleRestore = async (role: AdminCompanyRoleView) => {
    setBusyRoleKey(role.key);
    try {
      await companyRbacApi.restoreRole(role.key);
      showToast(`「${role.name}」 역할을 복구했습니다.`, 'success');
      await invalidateRoles();
    } catch (err) {
      showToast(companyRbacErrorMessage(err, '복구에 실패했습니다.'), 'error');
    } finally {
      setBusyRoleKey(null);
    }
  };

  const handleTogglePermission = async (key: string, orgAdjustable: boolean) => {
    setBusyPermissionKey(key);
    try {
      await companyRbacApi.updatePermission(key, { orgAdjustable });
      showToast(orgAdjustable ? `「${key}」를 조직이 조정할 수 있게 했습니다.` : `「${key}」를 시스템 값으로 고정했습니다.`, 'success');
      await queryClient.invalidateQueries({ queryKey: COMPANY_RBAC_QUERY_KEYS.permissions });
    } catch (err) {
      showToast(companyRbacErrorMessage(err, '변경에 실패했습니다.'), 'error');
      throw err;
    } finally {
      setBusyPermissionKey(null);
    }
  };

  const permissions = permissionsQuery.data ?? [];
  const roles = rolesQuery.data ?? [];
  const isLoading = permissionsQuery.isLoading || rolesQuery.isLoading;
  const loadError = permissionsQuery.error || rolesQuery.error;

  const tabs: { key: Tab; label: string }[] = [
    { key: 'roles', label: `시스템 역할 (${roles.length})` },
    { key: 'permissions', label: `권한 정의 (${permissions.length})` },
  ];

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">조직 역할·권한</h2>
          <p className="mt-1 text-sm text-gray-500">
            조직이 멤버에게 배정하는 시스템 역할과 권한 정의를 관리합니다. 관리자 역할(슈퍼관리자)과는 다른 축입니다.
          </p>
        </div>
        {tab === 'roles' && (
          <Button
            variant="primary"
            onClick={() => {
              setFormError(null);
              setFormMode({ kind: 'create' });
            }}
          >
            <span>+</span>
            <span>역할 추가</span>
          </Button>
        )}
      </div>

      <div className="mb-4 border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                tab === t.key
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="text-gray-500">로딩 중...</div>
        </div>
      ) : loadError ? (
        <div className="rounded bg-red-50 p-4 text-red-600">데이터를 불러오는데 실패했습니다.</div>
      ) : tab === 'roles' ? (
        <CompanyRoleTable
          roles={roles}
          permissionTotal={permissions.length}
          busyKey={busyRoleKey}
          onSelect={(role) => {
            setFormError(null);
            setFormMode({ kind: 'edit', role });
          }}
          onArchive={(role) => setPendingArchive(role)}
          onRestore={(role) => void handleRestore(role)}
        />
      ) : (
        <PermissionDefinitionsSection permissions={permissions} busyKey={busyPermissionKey} onToggle={handleTogglePermission} />
      )}

      {formMode && (
        <CompanyRoleFormModal
          // 대상이 바뀌면 remount — 열림 시점 스냅샷이 dirty 기준선이라 state를 이어 쓰면 안 된다
          key={formMode.kind === 'edit' ? formMode.role.key : 'create'}
          mode={formMode}
          permissions={permissions}
          busy={createMutation.isPending || updateMutation.isPending}
          errorMessage={formError}
          onCreate={(body) => createMutation.mutate(body)}
          onUpdate={(key, body) => updateMutation.mutate({ key, body })}
          onClose={() => {
            if (createMutation.isPending || updateMutation.isPending) return;
            setFormMode(null);
            setFormError(null);
          }}
        />
      )}

      <ConfirmModal
        open={pendingArchive !== null}
        title="역할 보관"
        body={
          pendingArchive
            ? `「${pendingArchive.name}」(${pendingArchive.key})을 보관합니다. 조직 화면 목록에서 사라지고 새로 배정할 수 없으며, 이 역할로 해소되던 권한은 없어집니다. 어느 조직에라도 활성 배정이 있으면 보관할 수 없습니다(${pendingArchive.holderCompanyCount}개 조직 · ${pendingArchive.holderCount}명 — 목록 조회 시점). 오버라이드한 조직(${pendingArchive.overrideCompanyCount})의 행은 그대로 남아 복구하면 다시 살아납니다.`
            : undefined
        }
        confirmLabel="보관"
        danger
        busy={busyRoleKey !== null}
        onConfirm={() => void handleArchive()}
        onClose={() => {
          if (busyRoleKey === null) setPendingArchive(null);
        }}
      />
    </div>
  );
}
